import { useFormik } from "formik";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonT1 } from "../../../../components/atoms/Button";
import { QuickCreateSelect } from "../../../shared/quickCreate";
import { ConfirmationModal } from "../../../../components/organisms/Modal/confirmationModal";
import { ManageGeneralFormProvider } from "../../../../components/organisms/Provider/ManageGeneralFormProvider";
import { useNotification } from "../../../../hooks/useNotification";
import {
  useAssignShopifyCatalogueBackorderMutation,
  useAssignShopifyCatalogueLocationsMutation,
  useAssignShopifyCataloguePricelistMutation,
  useGetShopifyWarehouseMappingsQuery,
  useListShopifyCatalogueProductsMutation,
  useSyncFromShopifyMutation,
  useSyncShopifyInventoryMutation,
  useUnlistShopifyCatalogueProductsMutation,
} from "../../../../store/apiSlice/shopifyApiSlice";
import { Button } from "../../../newLayout/page/components/Button/Button.jsx";
import { componentWithCondition } from "../../../newLayout/page/components/hoc/componentWithConditionCheck";
import { ButtonSpinner } from "../../../../components/atoms/Button/Button.styles";
import { ListActionBurgerMenu } from "../../../newLayout/page/components/OptionsForView/ListActionBurgerMenu";
import { ModalBodyDiv } from "./styles";

/**
 * Bulk catalogue toolbar actions for OptionsForView selection.
 *
 * @param {Object} props
 * @param {number|string} props.storeId Shopify store id from orderConfig.
 * @param {Array<Object>} props.selectedOrderFromListview Selected catalogue rows.
 * @param {Function} props.setSelectedOrder Clears / updates selection.
 * @return {JSX.Element}
 */
const CatalogueAction = ({ storeId, selectedOrderFromListview, setSelectedOrder }) => {
  const notify = useNotification();
  const selectedRows = useMemo(() => selectedOrderFromListview || [], [selectedOrderFromListview]);
  const selectedIds = useMemo(
    () => selectedRows.map(row => row.product_id || row.id).filter(Boolean),
    [selectedRows],
  );
  const hasSelection = selectedIds.length > 0;
  const clearSelection = () => setSelectedOrder([]);

  const [listProducts, { isLoading: isListing } = {}] = useListShopifyCatalogueProductsMutation();
  const [unlistProducts, { isLoading: isUnlisting } = {}] =
    useUnlistShopifyCatalogueProductsMutation();
  const [assignPricelist, { isLoading: isAssigningPricelist } = {}] =
    useAssignShopifyCataloguePricelistMutation();
  const [assignLocations, { isLoading: isAssigningLocations } = {}] =
    useAssignShopifyCatalogueLocationsMutation();
  const [assignBackorder, { isLoading: isAssigningBackorder } = {}] =
    useAssignShopifyCatalogueBackorderMutation();

  const { data: warehouseMappings = [] } = useGetShopifyWarehouseMappingsQuery(storeId, {
    skip: !storeId,
  });

  const [modal, setModal] = useState(null);
  const [warehouseIds, setWarehouseIds] = useState([]);
  const [backorderValue, setBackorderValue] = useState(true);

  const pricelistFormik = useFormik({
    initialValues: { catalogue_pricelist_id: "" },
    enableReinitialize: true,
  });

  const busy =
    isListing ||
    isUnlisting ||
    isAssigningPricelist ||
    isAssigningLocations ||
    isAssigningBackorder;

  const mappedWarehouses = useMemo(() => {
    const seen = new Set();
    return (warehouseMappings || []).reduce((acc, row) => {
      if (!row.warehouse_id || seen.has(row.warehouse_id)) return acc;
      seen.add(row.warehouse_id);
      acc.push({
        id: row.warehouse_id,
        name: row.warehouse_name || `Facility ${row.warehouse_id}`,
      });
      return acc;
    }, []);
  }, [warehouseMappings]);

  const closeModal = () => {
    setModal(null);
    setWarehouseIds([]);
    setBackorderValue(true);
    pricelistFormik.resetForm();
  };

  const toastResult = (detail, result) => {
    const failed = result?.failed || 0;
    const firstReason = result?.errors?.[0]?.reason;
    if (failed) {
      notify({
        type: "warning",
        message: firstReason
          ? `${detail} (${failed} failed: ${firstReason})`
          : `${detail} (${failed} failed)`,
      });
      return;
    }
    notify({ type: "success", message: detail || "Updated successfully." });
  };

  const runList = async () => {
    if (!hasSelection || busy || !storeId) return;
    try {
      const result = await listProducts({ id: storeId, product_ids: selectedIds }).unwrap();
      toastResult(result.detail, result);
      clearSelection();
    } catch (err) {
      notify({
        type: "error",
        message: err?.data?.detail || "Could not list products on Shopify.",
      });
    }
  };

  const runUnlist = async () => {
    if (!hasSelection || busy || !storeId) return;
    try {
      const result = await unlistProducts({ id: storeId, product_ids: selectedIds }).unwrap();
      toastResult(result.detail, result);
      clearSelection();
    } catch (err) {
      notify({
        type: "error",
        message: err?.data?.detail || "Could not unlist products from Shopify.",
      });
    }
  };

  const confirmAssignPricelist = async () => {
    const selectedPricelist = pricelistFormik.values.catalogue_pricelist_id;
    if (!selectedPricelist) {
      notify({ type: "error", message: "Please select a sales price list." });
      return;
    }
    try {
      const result = await assignPricelist({
        id: storeId,
        product_ids: selectedIds,
        pricelist_id: Number(selectedPricelist),
      }).unwrap();
      toastResult(result.detail, result);
      clearSelection();
      closeModal();
    } catch (err) {
      notify({
        type: "error",
        message: err?.data?.detail || "Could not assign pricelist.",
      });
    }
  };

  const confirmAssignLocations = async () => {
    if (!warehouseIds.length) {
      notify({ type: "error", message: "Please select at least one facility." });
      return;
    }
    try {
      const result = await assignLocations({
        id: storeId,
        product_ids: selectedIds,
        warehouse_ids: warehouseIds,
      }).unwrap();
      toastResult(result.detail, result);
      clearSelection();
      closeModal();
    } catch (err) {
      const msg = err?.data?.warehouse_ids || err?.data?.detail || "Could not assign locations.";
      notify({
        type: "error",
        message: Array.isArray(msg) ? msg.join(" ") : String(msg),
      });
    }
  };

  const confirmAssignBackorder = async () => {
    try {
      const result = await assignBackorder({
        id: storeId,
        product_ids: selectedIds,
        allow_backorder: backorderValue,
      }).unwrap();
      toastResult(result.detail, result);
      clearSelection();
      closeModal();
    } catch (err) {
      notify({
        type: "error",
        message: err?.data?.detail || "Could not update backorder setting.",
      });
    }
  };

  const toggleWarehouse = warehouseId => {
    setWarehouseIds(prev =>
      prev.includes(warehouseId) ? prev.filter(id => id !== warehouseId) : [...prev, warehouseId],
    );
  };

  const burgerItems = useMemo(
    () => [
      {
        key: "assign-pricelist",
        label: "Assign Pricelist",
        onClick: () => setModal("pricelist"),
        disabled: !hasSelection || busy,
        title: hasSelection ? "" : "Select at least one product to assign pricelist.",
        isLoading: isAssigningPricelist,
      },
      {
        key: "assign-locations",
        label: "Assign Locations",
        onClick: () => setModal("locations"),
        disabled: !hasSelection || busy,
        title: hasSelection ? "" : "Select at least one product to assign locations.",
        isLoading: isAssigningLocations,
      },
      {
        key: "allow-backorder",
        label: "Allow Backorder",
        onClick: () => setModal("backorder"),
        disabled: !hasSelection || busy,
        title: hasSelection ? "" : "Select at least one product to set backorder.",
        isLoading: isAssigningBackorder,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasSelection,
      busy,
      isAssigningPricelist,
      isAssigningLocations,
      isAssigningBackorder,
      selectedIds.length,
    ],
  );

  return (
    <>
      <li
        className={`tableButton ${hasSelection && !busy && !isListing ? "activeState" : "disableState"}`}
      >
        <button
          onClick={runList}
          disabled={!hasSelection || busy}
          title={hasSelection ? "" : "Select at least one product to list."}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isListing && <ButtonSpinner aria-hidden="true" />}
          {isListing ? "Listing…" : "List"}
        </button>
      </li>
      <li
        className={`tableButton ${hasSelection && !busy && !isUnlisting ? "activeState" : "disableState"}`}
      >
        <button
          onClick={runUnlist}
          disabled={!hasSelection || busy}
          title={hasSelection ? "" : "Select at least one product to unlist."}
          style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
        >
          {isUnlisting && <ButtonSpinner aria-hidden="true" />}
          {isUnlisting ? "Unlisting…" : "Unlist"}
        </button>
      </li>

      <ListActionBurgerMenu items={burgerItems} />

      {modal === "pricelist" && (
        <ConfirmationModal
          closeModal={closeModal}
          handleModalWork={confirmAssignPricelist}
          titleOfModal="Assign Pricelist"
          btnName="Assign"
          isLoading={isAssigningPricelist}
          description={
            <ManageGeneralFormProvider formik={pricelistFormik}>
              <ModalBodyDiv>
                <div className="hint">
                  Choose the sales price list used for pricing the {selectedIds.length} selected
                  product(s). Store catalogue eligibility is unchanged.
                </div>
                <QuickCreateSelect
                  createKind="pricelist"
                  name="catalogue_pricelist_id"
                  moduleName="pricelist"
                  label="Sales Price List"
                  placeholder="Select price list"
                  context="general"
                />
              </ModalBodyDiv>
            </ManageGeneralFormProvider>
          }
        />
      )}

      {modal === "locations" && (
        <ConfirmationModal
          closeModal={closeModal}
          handleModalWork={confirmAssignLocations}
          titleOfModal="Assign Locations"
          btnName="Assign"
          isLoading={isAssigningLocations}
          description={
            <ModalBodyDiv>
              <div className="hint">
                Select facilities that should sync stock for the selected products. Only facilities
                mapped to this store are shown.
              </div>
              {!mappedWarehouses.length && (
                <div className="hint">No mapped facilities yet. Finish Location Mapping first.</div>
              )}
              {mappedWarehouses.map(wh => (
                <label className="warehouseOption" key={wh.id}>
                  <input
                    type="checkbox"
                    checked={warehouseIds.includes(wh.id)}
                    onChange={() => toggleWarehouse(wh.id)}
                  />
                  <span>{wh.name}</span>
                </label>
              ))}
            </ModalBodyDiv>
          }
        />
      )}

      {modal === "backorder" && (
        <ConfirmationModal
          closeModal={closeModal}
          handleModalWork={confirmAssignBackorder}
          titleOfModal="Allow Backorder"
          btnName="Apply"
          isLoading={isAssigningBackorder}
          description={
            <ModalBodyDiv>
              <div className="hint">
                Set backorder for {selectedIds.length} selected product(s). This does not change the
                store-level Allow Backorders setting.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <ButtonT1
                  title="Off"
                  $variant={backorderValue ? "primaryLight" : "primary"}
                  onClickCallback={() => setBackorderValue(false)}
                />
                <ButtonT1
                  title="On"
                  $variant={backorderValue ? "primary" : "primaryLight"}
                  onClickCallback={() => setBackorderValue(true)}
                />
              </div>
            </ModalBodyDiv>
          }
        />
      )}
    </>
  );
};

export const CatalogueRightAction = ({ storeId }) => {
  const notify = useNotification();
  const [syncInventory, { isLoading }] = useSyncShopifyInventoryMutation();

  const handleSyncInventory = async () => {
    if (!storeId) return;
    try {
      const res = await syncInventory(storeId).unwrap();
      notify({ type: "success", message: res?.detail || "Inventory sync started." });
    } catch (error) {
      notify({ type: "error", message: error?.data?.detail || "Failed to start inventory sync." });
    }
  };

  if (!storeId || Number.isNaN(storeId) || storeId <= 0) return null;

  return (
    <Button
      btnName={isLoading ? "Syncing..." : "Sync Inventory"}
      classOfBtn=""
      handler={handleSyncInventory}
      isDisabledButton={isLoading}
    />
  );
};

/**
 * Toolbar actions for stores where Shopify owns the catalogue.
 *
 * @param {Object} props
 * @param {number} props.storeId Shopify store id from orderConfig.
 * @param {boolean} props.isSyncRunning True while a started sync has not reported back.
 * @param {Function} props.onSyncStarted Called after the sync is queued.
 * @return {JSX.Element|null}
 */
export const ShopifyLinkedProductsRightAction = ({ storeId, isSyncRunning, onSyncStarted }) => {
  const notify = useNotification();
  const navigate = useNavigate();
  const [syncFromShopify, { isLoading }] = useSyncFromShopifyMutation();

  if (!storeId || Number.isNaN(storeId) || storeId <= 0) return null;

  const handleSync = async () => {
    try {
      const res = await syncFromShopify(storeId).unwrap();
      notify({ type: "success", message: res?.detail || "Sync started." });
      onSyncStarted?.();
    } catch (error) {
      notify({ type: "error", message: error?.data?.detail || "Could not start the sync." });
    }
  };

  const busy = isLoading || isSyncRunning;

  return (
    <>
      <div
        className="milestoneBtn"
        role="button"
        tabIndex={0}
        onClick={() => navigate(`/integration/shopify/${storeId}/settings`)}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            navigate(`/integration/shopify/${storeId}/settings`);
          }
        }}
      >
        Store Settings
      </div>
      <span data-guide="shopify.catalogue.sync" style={{ display: "inline-flex" }}>
        <Button
          btnName={busy ? "Syncing..." : "Sync from Shopify"}
          classOfBtn=""
          handler={handleSync}
          isDisabledButton={busy}
          titleForActive="Import new Shopify products, read stock and update purchase orders in Shopify"
        />
      </span>
    </>
  );
};

export const CatalogueActionWithCheck = componentWithCondition(CatalogueAction);
